/** @jsx jsx */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { jsx } from '@emotion/core';
import {
  ExpansionPanelSummary,
  ExpansionPanelDetails,
} from '@material-ui/core';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { ExpansionPanel } from '@material-ui/core';
import styles from './styles';
import { composeStyles } from 'features/Theme';

interface PropTypes {
  /**
   * Optional. If not provided, the component is "uncontrolled" and will
   * maintain its own expanded/collapsed state
   */
  expanded?: boolean;
  /**
   * Use for ID, so should be unique on the page.
   */
  panelName: string;
  /**
   * Used to hide the expanded toggle icon
   */
  hideToggleIcon?: boolean;

  title: React.ReactNode;
  expandedAltText?: string;
  collapsedAltText?: string;
  footer?: React.ReactNode;
  banner?: React.ReactNode;
  bannerSecondaryText?: React.ReactNode;
  onChange?: (
    event: React.ChangeEvent<{}>,
    panelName: string,
    expanded: boolean,
  ) => void;
}

const ExpandablePanel: React.FC<PropTypes> = props => {
  const {
    expanded,
    panelName,
    hideToggleIcon,
    onChange,
    title,
    children,
    banner,
    bannerSecondaryText,
    footer,
    expandedAltText,
    collapsedAltText,
  } = props;
  const summaryId = `${panelName}-summary`;
  const ariaControlsId = `${panelName}-aria-controls`;
  const detailId = `${panelName}-detail`;

  const [isExpanded, setIsExpanded] = useState<boolean>(expanded || false);

  useEffect(() => {
    if (typeof expanded !== 'undefined') {
      setIsExpanded(expanded);
    }
  }, [expanded]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<{}>, newExpanded: boolean) => {
      if (typeof expanded === 'undefined') {
        setIsExpanded(isExpanded => !isExpanded);
      }
      if (onChange) {
        onChange(e, panelName, newExpanded);
      }
    },
    [expanded, onChange, panelName],
  );

  const altText = useMemo(
    () => (isExpanded ? expandedAltText : collapsedAltText),
    [collapsedAltText, expandedAltText, isExpanded],
  );

  const expandable = Boolean(children);

  const summary = useMemo(() => {
    if (expandable) {
      return (
        <ExpansionPanelSummary
          aria-controls={ariaControlsId}
          id={summaryId}
          expandIcon={hideToggleIcon ? null : <ExpandMoreIcon />}
          title={altText}
        >
          {title}
        </ExpansionPanelSummary>
      );
    } else {
      return <div css={styles.nonExpandableSummary}>{title}</div>;
    }
  }, [altText, ariaControlsId, expandable, hideToggleIcon, summaryId, title]);

  return (
    <React.Fragment>
      <div css={composeStyles([styles.panel, isExpanded && styles.expanded])}>
        {!bannerSecondaryText && !banner ? null : (
          <div css={styles.banner}>
            {banner ? <React.Fragment>{banner}</React.Fragment> : null}
            {bannerSecondaryText ? (
              <div css={styles.bannerSecondaryText}>{bannerSecondaryText}</div>
            ) : null}
          </div>
        )}
        <ExpansionPanel
          expanded={isExpanded}
          onChange={handleChange}
          elevation={0}
        >
          {summary}
          {expandable ? (
            <ExpansionPanelDetails id={detailId} css={styles.expansionDetails}>
              <div css={styles.expansionDetailsContent}>{children}</div>
              {footer ? <div css={styles.footer}>{footer}</div> : null}
            </ExpansionPanelDetails>
          ) : null}
        </ExpansionPanel>
      </div>
    </React.Fragment>
  );
};

export default ExpandablePanel;
